import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useLogicQualitySingleCheckLogic } from 'components/widgets/LogicQualitySingleCheck/LogicQualitySingleCheck.logic';
import classes from 'components/widgets/LogicQualitySingleCheck/LogicQualitySingleCheck.module.scss';
import { useLogicQualitySingleCheckProvider } from 'components/widgets/LogicQualitySingleCheck/LogicQualitySingleCheck.provider';
import { ValidationOverview } from 'components/widgets/LogicQualitySingleCheck/components/validationOverview/ValidationOverview';
import { logicQualityTitleToComponentKeyMap } from 'components/widgets/ValidationTable/ValidationTable.utils';
import { WholeProjectRowComponent } from 'components/widgets/WholeProjectRow/components/WholeProjectRowComponent';

export interface Props extends IComponentProps {
    check: string;
}

export const LogicQualitySingleCheck = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title,
    check,
    setIsPayloadReady,
}: Props) => {
    const effectiveTitle = title ?? check ?? 'Quality check';
    const componentKeyByCheck = logicQualityTitleToComponentKeyMap[check];
    const widgetsConfig = useConfigurationStore();
    const config = widgetsConfig[componentKeyByCheck as keyof typeof widgetsConfig] as any;
    const { componentKey, routeId: id, endpoints } = config;

    const { ui, api } = useLogicQualitySingleCheckProvider({
        widgetViewMode,
        localFilters,
        globalFilterData,
        latestVersionId,
        projectId,
        componentKey,
        contractId,
        check,
        widgetId,
        endpoints,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: effectiveTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getLogicCheckerCategoryResults.payload,
        widgetEndpoint: api.getLogicCheckerCategoryResults.endpoint,
    });

    const { logic } = useLogicQualitySingleCheckLogic({
        ui,
        api,
        check,
        setIsPayloadReady,
        widgetViewMode,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState: logic.tableInitialState,
        columns: logic.generatedCols,
        widgetViewMode,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown key={'export'} title={effectiveTitle} subTitle={subTitle} />
                ),
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={effectiveTitle}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
            ]}
        >
            <div className={classes.logicQualitySingleCheckWrapper}>
                {logic.wholeProjectRow && (
                    <WholeProjectRowComponent
                        row={logic.wholeProjectRow as unknown}
                        validation={logic.validation}
                        columns={logic.generatedCols}
                        check={check}
                        versionHashCode={ui.vars.latestVersionHashCode}
                    />
                )}

                <div className={classes.bottomSection}>
                    <ValidationOverview
                        validationOverview={logic.validationOverview}
                        isIncludedInOverallScore={logic.validation?.mandatory}
                        threshold={ui.vars.threshold}
                    />

                    <TableV8
                        columns={logic.generatedCols}
                        data={logic.generatedData}
                        initialState={logic.tableInitialState}
                        isLoading={logic.isLoadingTableData}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        manualSorting={false}
                    />
                </div>
            </div>
        </WidgetWithTitle>
    );
};
